import { login, loginByOuterSys, logout as logoutApi } from '@/api/user'
import { LoginByOuterSys, LoginParams, LoginResponse, MenuRecord } from '@/api/user/model'
import { defineStore } from 'pinia'
import store from '@/store'
import {
  TOKEN_KEY,
  USER_INFO_KEY,
  AUTH_KEY,
  MENU_KEY,
  PROVIDER_KEY,
  GUIDE_KEY,
  ACCOUNT_KEY,
  ROLE_KEY,
  OUTER_TOKEN_KEY,
  SRM_USER_INFO_KEY
} from '@/enums/cache'
import { queryProviderOption } from '@/api/common'
import { ProviderListItem } from '@/api/common/type'
import { SrmUserInfo } from '@/api/index-page/type'
import { clearAuthCache, getAuthCache, setAuthCache } from '@/utils/auth'
import { getTokenFrom, isReferrerFromAuthBackup, TOKEN_FROM, toOuterLogin } from '@/utils/login'
import { Role } from '@/api/account-management/model'
import { UserInfo } from '@/api/user/model.d'
import { getRole } from '@/api/account-management'
import { getMessageNotificationCount } from '@/api/notice-center'
import { getSrmUserInfo } from '@/api/index-page'
import { OUTER_USER_TYPE } from '@/views/UserAccounts/js/config'
import { BUSINESS_TYPE } from '@/common/constants'
import {
  getLpToken,
  setBasisToken,
  setLpToken,
  CASH_WITHDRAW_MANAGEMENT_PATH,
  PAY_CENTER_PATH
} from '@/utils/micro-app'
import { BOOLEAN } from '@/utils/constants'

interface UserState {
  userInfo: Nullable<UserInfo>
  token?: string
  auths: string[]
  showGuide: boolean | null
  menus: MenuRecord[]
  providers: ProviderListItem[]
  account: string
  role: Nullable<Role>
  /** 总的未读消息数量 */
  unreadNoticeCount: number
  /** 供应商中台用户信息 */
  srmUserInfo: Partial<SrmUserInfo>
}

export const useUserStore = defineStore({
  id: 'user',
  state: (): UserState => ({
    userInfo: null,
    token: undefined,
    auths: [],
    menus: [],
    showGuide: false,
    providers: [],
    account: '',
    role: null,
    unreadNoticeCount: 0,
    srmUserInfo: {}
  }),
  getters: {
    getUserInfo(): Nullable<UserInfo> {
      return this.userInfo || getAuthCache(USER_INFO_KEY)
    },
    /** 合作业务类型 是否为 非摇钱树 */
    getIsUserNotScw(): boolean {
      const userInfo = this.getUserInfo
      const getBusinessTypeList = userInfo?.businessTypeList || []
      return !getBusinessTypeList.includes(BUSINESS_TYPE.CASH_COW)
    },
    getToken(): string | undefined {
      return this.token || getAuthCache(TOKEN_KEY)
    },
    getAuths(): () => string[] {
      // 无缓存
      return () => getAuthCache(AUTH_KEY) || this.auths || []
    },
    getMenus(): MenuRecord[] {
      return getAuthCache(MENU_KEY) || this.menus || []
    },
    getProviders(): ProviderListItem[] {
      return (
        (getAuthCache(PROVIDER_KEY) as ProviderListItem[])?.map((item) => ({
          ...item,
          providerId: Number(item.providerId)
        })) ||
        this.providers ||
        []
      )
    },
    getAccount(): string {
      return this.account || getAuthCache(ACCOUNT_KEY) || ''
    },
    getRole(): Nullable<Role> {
      return this.role || getAuthCache(ROLE_KEY) || null
    },
    /** 是否LEYOKEY内部用户 */
    getIsLKUser(): boolean {
      return this.getUserInfo?.outerUserType === OUTER_USER_TYPE.LEYOKEY
    },
    /** 是否SRM供应商用户 */
    getIsSrmUser(): boolean {
      return this.getUserInfo?.outerUserType === OUTER_USER_TYPE.SRM
    },
    /** 获取总的未读消息数量 */
    getUnreadNoticeCount(): number {
      return this.unreadNoticeCount
    },
    getSrmAccountInfo(): Partial<SrmUserInfo> {
      return this.srmUserInfo || getAuthCache(SRM_USER_INFO_KEY) || {}
    }
  },
  actions: {
    setToken(token: string | undefined) {
      this.token = token ?? ''
      setAuthCache(TOKEN_KEY, token)
    },
    setUserInfo(userInfo: Nullable<UserInfo>) {
      this.userInfo = userInfo
      setAuthCache(USER_INFO_KEY, userInfo)
    },
    setAuths(auths: string[]) {
      this.auths = auths
      setAuthCache(AUTH_KEY, auths)
    },
    setMenus(menus: MenuRecord[]) {
      this.menus = menus
      setAuthCache(MENU_KEY, menus)
    },
    setShowGuide(showGuide: boolean | null) {
      this.showGuide = showGuide
      setAuthCache(GUIDE_KEY, showGuide)
    },
    setProviders(providers: ProviderListItem[]) {
      this.providers = providers
      setAuthCache(PROVIDER_KEY, providers)
    },
    setAccount(account: string) {
      this.account = account
      setAuthCache(ACCOUNT_KEY, account)
    },
    setRole(role: Role | null) {
      this.role = role
      setAuthCache(ROLE_KEY, role)
    },
    setSrmUserInfo(srmUserInfo: SrmUserInfo) {
      this.srmUserInfo = srmUserInfo
      setAuthCache(SRM_USER_INFO_KEY, srmUserInfo)
    },
    resetState() {
      this.userInfo = null
      this.token = undefined
      this.auths = []
      this.menus = []
      this.providers = []
      this.showGuide = false
      this.account = ''
      this.role = null
      clearAuthCache()
      setBasisToken('')
      setLpToken('')
    },
    /**
     * 执行登录请求
     * @returns 登录信息
     */
    async login(params: LoginParams) {
      try {
        const { account } = params
        this.setAccount(account)
        const data = await login(params)
        const { userInfo } = data
        const { token } = userInfo
        this.setToken(token)
        return data
      } catch (error) {
        return Promise.reject(error)
      }
    },
    /**
     * 执行外部跳入登录
     * @returns 登录信息
     */
    async outerLogin(params: LoginByOuterSys) {
      try {
        const data = await loginByOuterSys(params)
        const { userInfo } = data
        const { token, uname } = userInfo
        this.setAccount(uname)
        this.setToken(token)
        return data
      } catch (error) {
        return Promise.reject(error)
      }
    },
    /**
     * 解析登录信息，初始化系统
     */
    async loadLoginInfo(data: LoginResponse) {
      try {
        const { buttonModule, menuList, userInfo } = data
        this.setUserInfo(userInfo)
        this.setAuths(buttonModule)
        this.setMenus(menuList)
        this.getSrmUserInfo()
        await this.afterLoginAction()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async afterLoginAction() {
      // const proviers = await queryProviderOption()
      const [providers, role] = await Promise.all([
        queryProviderOption(),
        getRole({ id: Number(this.getUserInfo?.roleId) })
      ])
      this.setProviders(providers)
      this.setRole(role)
    },
    async logout() {
      if (this.getToken) {
        try {
          await logoutApi()
        } catch (error) {
          console.error(error)
        }
      }
      this.resetState()
      toOuterLogin()
    },
    /** 获取总的未读消息数量 */
    async fetchUnreadNoticeCount() {
      try {
        const { notReadCount = 0 } = await getMessageNotificationCount({})
        this.unreadNoticeCount = notReadCount
      } catch (error) {
        console.log(error)
      }
    },
    /**
     * 使用 loginByOuterSys 重新拉取菜单并刷新页面
     * 入参从缓存获取：outerToken(OUTER_TOKEN_KEY)、outerUserType(getTokenFrom)、payCenterToken(getLpToken)
     */
    async refreshMenuAndReload() {
      const outerToken = getAuthCache(OUTER_TOKEN_KEY) as string
      if (!outerToken) {
        GMessage.error('无法刷新菜单：缺少外部登录态，请重新登录')
        return
      }
      const from = getTokenFrom()
      const outerUserType =
        from === TOKEN_FROM.LEYOKEY ? OUTER_USER_TYPE.LEYOKEY : OUTER_USER_TYPE.SRM
      const payCenterToken = getLpToken() ?? ''
      const params: LoginByOuterSys = {
        outerToken,
        outerUserType,
        beAuth8: isReferrerFromAuthBackup() ? BOOLEAN.YES : BOOLEAN.NO,
        payCenterToken
      }
      try {
        const data = await loginByOuterSys(params)
        await this.loadLoginInfo(data)
        const targetUrl = `${window.location.origin}${PAY_CENTER_PATH}${CASH_WITHDRAW_MANAGEMENT_PATH}`
        // 待优化：强制刷新是为了触发ice-lab/icestark重载
        window.location.replace(targetUrl)
        window.location.reload()
      } catch (error) {
        console.error('[refreshMenuAndReload]', error)
        const err = error as Error
        GMessage.error(err?.message || '菜单刷新失败，请稍后重试或重新登录')
      }
    },
    async getSrmUserInfo() {
      const data = await getSrmUserInfo()
      this.setSrmUserInfo(data.userInfo)
    }
  }
})

export function useUserStoreWithoutSetup() {
  return useUserStore(store)
}
